// `configureDictationData` — the injected-config seam (D1, ARCFE-1084; the
// `configureIntegrationsData` / `configureTemplatesData` / `configureCodingData`
// pattern), born fail-loud exactly as the child change's D1.2 line asks.
//
// WHY A SEAM AND NOT THE `@heidi/api/client` REGISTRY. The registry resolves the
// SCRIBE client family — clients pointed at ml-scribe, sharing its interceptors,
// its base URL and its auth contract. `t11nClient` is none of those: it is a
// dedicated axios instance for the standalone **transcription service**, based
// at a REGION-RESOLVED host (`getTranscriptionBaseUrl` — AWS/Azure subdomain
// substitution, on-prem origin, PR-preview ingress), with its own `X-Country`
// header, its own workspace-id header, the desktop confirmed-offline
// short-circuit and the app's tracing headers. Registering a second service's
// client in the scribe registry would make "resolve a scribe client" able to
// return something that is not one, which is a worse defect than the wiring it
// saves. This is the ratified `inboxHttp` shape (integrations C/D, ARCFE-1016),
// which `packages/templates/src/data/config.ts` already names as its precedent.
//
// The builder cannot move either way — and this is the whole reason D1 was a
// cross-lane decision rather than a carve. `src/lib/SessionRecordingProvider/t11nClient.ts`
// reads eight app-side modules (`getRegion`, `getCurrentUser`, `isOnPrem`, the
// endpoints helpers, the client-identity headers, the desktop offline
// short-circuit, the sync auth-token helper, the tracing headers) and holds the
// LIVE-TRANSCRIPTION surface on the same module. It stays app-side, untouched,
// and what crosses is a LOADER for it rather than the built instance — see
// `loadT11nHttp` below for why that distinction is a build-correctness
// requirement and not a style choice.
//
// WHAT IS DELIBERATELY *NOT* ON THIS SEAM, so the omission is read as a
// measurement rather than an oversight:
//
//   * the query client as an INSTANCE (`queryClient: QueryClient`, the shape
//     five other packages carry). D1.2 anticipated it and this seam declined it
//     on measurement — both delete mutations call `useQueryClient()` in the hook
//     body and `invalidateDictationLogsStats` takes its `QueryClient` as an
//     explicit parameter. That measurement still holds for every carved module.
//     What is declared below instead is the GETTER, which is a different
//     commitment: see `getQueryClient`.
//   * any workspace command. The D3 crossing rule stands: the transcribe-mode
//     preference is a workspace user-record write and this package never
//     receives it as an injected foreign command (design D7's hidden-command
//     ban).

import { type QueryClient } from '@tanstack/react-query';

import type {
  DictationLocaleMessagesLoader,
  UseDictationSupportedLanguages,
} from '../contracts';

/**
 * Minimal structural view of the transcription-service client.
 *
 * Kept structural so this package's public signatures never name axios types
 * (declaration portability in the standalone build) — the templates
 * `CommunityHttpClient` rule. Every method here is one the carved api module
 * actually calls; nothing is declared speculatively. `get` carries `timeout`
 * because `getDictationStats` passes a per-request deadline, and that deadline
 * is behaviour (it is what produces the "–" fallback instead of an endless
 * skeleton).
 */
export interface T11nHttpClient {
  get<T>(
    url: string,
    config?: {
      params?: Record<string, string | number | undefined>;
      timeout?: number;
    }
  ): Promise<{ data: T }>;
  post<T>(url: string, body?: unknown): Promise<{ data: T }>;
  put<T>(
    url: string,
    body?: unknown,
    config?: { timeout?: number }
  ): Promise<{ data: T }>;
  delete<T = unknown>(url: string): Promise<{ data: T }>;
}

export interface DictationDataConfig {
  /**
   * Returns the app's singleton `QueryClient`.
   *
   * The ONE way the app cache crosses into this package. A registry accessor on
   * a platform door was the alternative and was rejected: it would make the
   * app's cache an ambient capability any package could import silently, which
   * is exactly the coupling a `configure<Domain>Data` member exists to declare.
   * The disposition is recorded under task 2.7 of
   * `openspec/changes/monorepo-ui-endgame/tasks.md`.
   *
   * A GETTER, not the instance, and that is the whole commitment: it resolves
   * per call, so wiring order can never race client construction and a
   * module-scope consumer may be evaluated before `configureDictationData` has
   * run. The instance field five other packages hold cannot demonstrate that —
   * reading the field IS the resolution.
   *
   * OPTIONAL, unlike the `session` member it copies, and only because this
   * package has no consumer for it yet. The two module-scope legs still
   * app-side (`dictationBubble/store/createDictationSlice.ts` and
   * `getDictationContext.ts`) adopt it when their modules move; requiring it
   * today would make eleven existing test doubles hand over a cache identity
   * none of them reads. The app wires it unconditionally
   * (`src/composition/hosts/dictation/dictation-data-wiring.ts`) and that module's test pins the key
   * list by equality, so a dropped wiring is a failing test rather than a
   * silent `undefined`. THE FIRST CONSUMER MAKES IT REQUIRED, in its own PR: a
   * `getQueryClient?.()` resolving to `undefined` would hand a module-scope
   * invalidator no cache and no error.
   *
   * Heals when nothing in this package's cone needs a cache handle outside a
   * render — when the last module-scope leg either becomes a hook calling
   * `useQueryClient()` or takes its `QueryClient` as an explicit parameter, the
   * shape `invalidateDictationLogsStats` already uses.
   */
  getQueryClient?: () => QueryClient;
  /**
   * LOADER for the app-built transcription-service client — deliberately a
   * function, not the client itself.
   *
   * A different service from ml-scribe, which is why it is injected rather than
   * resolved from the scribe registry. The indirection on top of that is a
   * BUILD-CORRECTNESS requirement, learned the hard way on the integrations
   * lane (ARCFE-1016) and inherited here rather than re-learned:
   *
   * the app-side builder's module cone reaches `@/hooks/query/useQueryUser`,
   * the sync auth-token helper and `@heidi/workspace/data`, which in turn reach
   * the Rematch store. The store is an ASYNC module under webpack/rspack (it
   * awaits its logger and model barrel before `init()`), and async-module status
   * is contagious to every importer. Handing the built client to
   * `configureDictationData` would force the wiring module — and therefore
   * `_app` — to pull that whole cone at module scope, which is how integrations
   * produced `ReferenceError: Cannot access 'v' before initialization` during
   * "Collecting page data", on a page with no connection to the domain.
   *
   * A loader also restores the pre-carve timing: before the move, the five
   * log-CRUD fns closed over a module-scope singleton in a module that was only
   * evaluated when something reached for it.
   *
   * Nothing local catches this class — tsc, jest and lint are all blind to
   * module-initialisation order in a bundled chunk — so the shape is the guard.
   *
   * May return the client or a promise of it; the façade awaits either, so
   * tests can pass a plain `() => double` and the app can pass a dynamic
   * `import()`.
   */
  loadT11nHttp: () => T11nHttpClient | Promise<T11nHttpClient>;
  /**
   * Reactive read of the raw auth token.
   *
   * Injected rather than imported because `/data` may not reach `@/store` or
   * `react-redux` (severance cohort S2). IT IS A HOOK and it is REACTIVE, which
   * is the behaviour-preserving choice HERE specifically — unlike the
   * preferences carve, which took a boolean `enabled` because its pre-move read
   * was a non-subscribing `store.getState()`. `useDictationLogs`'s pre-move read
   * was `useSelector((state: RootState) => state.auth.token)`, and the app
   * injects `useAuthToken` from `@/hooks/useIsAuthenticated`, which is that
   * exact expression. Same subscription, same re-render timing.
   *
   * The token is not only an `enabled` gate here: the hook also returns
   * `isPending: !token || …` so the skeleton stays up through the pre-auth
   * window. A boolean `enabled` input could not express that, which is why this
   * cohort takes the seam and the preferences cohort did not.
   */
  useAuthToken: () => string | null | undefined;
  /**
   * The app's supported-language catalog —
   * `@/hooks/query/useQuerySupportedLanguages`, bound straight through.
   *
   * It is here rather than behind a door because there is no door: the base
   * query's core did move (`@heidi/session/data`), but reading it from this
   * package would mean a `@heidi/dictate` -> `@heidi/session` edge and a second
   * answer to "is the user authenticated" — the app hook resolves that verdict
   * before calling the core. A language VOCABULARY is also not this domain's
   * contract to own.
   *
   * A HOOK for the reason `useAuthToken` records: the catalog resolves while
   * the app runs, and the two readers format language names on every render.
   *
   * OPTIONAL and fail-loud at the call, like the member above — its two readers
   * are both in the verbal-commands tab, and the existing test doubles hand
   * over no catalog.
   */
  useSupportedLanguages?: UseDictationSupportedLanguages;
  /**
   * Loads one app locale's ICU catalog — `localeMessageLoaders` plus
   * `toIntlMessages` from `@/utils/i18n/localeMessageLoaders`.
   *
   * The bundles under `src/locales/**` are written by the Crowdin pipeline and
   * code-split by `next`, so they are app-owned by construction and the loader
   * table cannot cross. What crosses is one fetch: the locale ALIAS resolution
   * stays in-package over `@heidi/utils/i18n`, which is all
   * `@/utils/i18n/supportedLocales` ever was.
   *
   * ASYNC and per-call, never a preloaded map, because that is what the
   * dynamic `import()` behind each loader buys: a reader asking for German
   * pulls the German chunk and nothing else.
   *
   * OPTIONAL and fail-loud at the call. One reader —
   * `ui/logs/components/verbal-commands/hooks/useIntlForLanguage.ts`.
   */
  loadLocaleMessages?: DictationLocaleMessagesLoader;
}

let config: DictationDataConfig | null = null;

export const configureDictationData = (next: DictationDataConfig): void => {
  config = next;
};

/** Fail loudly on unconfigured use — never silently degrade. */
export const getDictationDataConfig = (): DictationDataConfig => {
  if (!config) {
    throw new Error(
      '[dictation/data] not configured — app bootstrap must call ' +
        'configureDictationData() (see src/composition/hosts/dictation/dictation-data-wiring.ts); ' +
        'tests exercising real dictation data modules must configure a test ' +
        'double first.'
    );
  }
  return config;
};

/** Test-only reset so a suite cannot leak a double into the next one. */
export const resetDictationDataConfigForTests = (): void => {
  config = null;
};
